import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { PictureAsPdf as PdfIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import vehicleService from '../../services/vehicleService';
import { Vehicle } from '../../types';
import ProposalPDF, { ProposalFormData } from './ProposalPDF';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v ?? '').trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrencyBR = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const defaultForm: ProposalFormData = {
  customerName: '',
  customerPhone: '',
  customerDocument: '',
  validUntil: '',
  vehiclePrice: 0,
  discount: 0,
  downPayment: 0,
  tradeInValue: 0,
  fees: 0,
  installmentsCount: 0,
  installmentsValue: 0,
  notes: '',
};

const ProposalPage: React.FC = () => {
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<ProposalFormData>(defaultForm);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const getFullImageUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!API_BASE) return url;
    return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const loadVehicles = async () => {
    try {
      setLoadingVehicles(true);
      setError(null);
      const all = await vehicleService.getAllVehicles();
      const available = (all || []).filter((v) => v.status === 'available');
      setVehicles(available);
    } catch (e: any) {
      console.error(e);
      setError('Não foi possível carregar veículos para a proposta.');
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedVehicle) return;
    const suggested = toNum(selectedVehicle.sale_price_promo || selectedVehicle.sale_price || 0);
    setForm((prev) => ({
      ...prev,
      vehiclePrice: prev.vehiclePrice > 0 ? prev.vehiclePrice : suggested,
    }));
  }, [selectedVehicle]);

  const computed = useMemo(() => {
    const vehiclePrice = Math.max(0, toNum(form.vehiclePrice));
    const discount = Math.max(0, toNum(form.discount));
    const tradeInValue = Math.max(0, toNum(form.tradeInValue));
    const fees = Math.max(0, toNum(form.fees));
    const total = Math.max(0, vehiclePrice - discount - tradeInValue + fees);
    const downPayment = Math.min(Math.max(0, toNum(form.downPayment)), total);
    const remaining = Math.max(0, total - downPayment);
    const installmentsCount = Math.max(0, Math.floor(toNum(form.installmentsCount)));
    const installmentsValue = Math.max(0, toNum(form.installmentsValue));
    const autoInstallmentValue = installmentsCount > 0 ? remaining / installmentsCount : 0;
    const finalInstallmentValue = installmentsValue > 0 ? installmentsValue : autoInstallmentValue;
    return {
      vehiclePrice,
      discount,
      tradeInValue,
      fees,
      total,
      downPayment,
      remaining,
      installmentsCount,
      finalInstallmentValue,
    };
  }, [form]);

  const canExportPdf = Boolean(selectedVehicle && form.customerName.trim() && computed.total >= 0);

  const handleDownloadPdf = async () => {
    if (!selectedVehicle) return;
    if (!form.customerName.trim()) {
      setError('Informe o nome do cliente para gerar a proposta.');
      return;
    }

    setDownloadingPdf(true);
    setError(null);
    try {
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;
      const vehicleImageUrl = getFullImageUrl(selectedVehicle.images?.[0]?.url) || null;

      const doc = (
        <ProposalPDF
          vehicle={selectedVehicle}
          vehicleImageUrl={vehicleImageUrl}
          logoUrl={logoUrl}
          generatedAtLabel={generatedAtLabel}
          proposalNumber={`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(
            selectedVehicle.id
          ).padStart(4, '0')}`}
          data={form}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeClient = form.customerName.trim().replace(/[^\p{L}\p{N}\s_-]+/gu, '').replace(/\s+/g, '-');
      a.download = `proposta-${safeClient || 'cliente'}-veiculo-${selectedVehicle.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Erro ao gerar PDF da proposta.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Propostas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 950 }}>
            Monte uma proposta comercial e exporte em PDF com o logo da empresa. Não salva no servidor — apenas consultas e geração local.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={loadingVehicles ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={loadVehicles}
            disabled={loadingVehicles}
          >
            {loadingVehicles ? 'Carregando...' : 'Atualizar veículos'}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={downloadingPdf ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />}
            onClick={handleDownloadPdf}
            disabled={!canExportPdf || downloadingPdf}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Dados da proposta
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  loading={loadingVehicles}
                  options={vehicles}
                  value={selectedVehicle}
                  onChange={(_, v) => setSelectedVehicle(v)}
                  getOptionLabel={(v) => `${v.brand} ${v.model} (${v.year}) — ${String((v as any).plate || (v as any).identification || v.id)}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Veículo"
                      placeholder="Selecione um veículo disponível"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingVehicles ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cliente"
                  value={form.customerName}
                  onChange={(e) => setForm((p) => ({ ...p, customerName: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Telefone"
                  value={form.customerPhone || ''}
                  onChange={(e) => setForm((p) => ({ ...p, customerPhone: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="CPF/CNPJ"
                  value={form.customerDocument || ''}
                  onChange={(e) => setForm((p) => ({ ...p, customerDocument: e.target.value }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Validade (até)"
                  type="date"
                  value={form.validUntil || ''}
                  onChange={(e) => setForm((p) => ({ ...p, validUntil: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Valor do veículo"
                  value={form.vehiclePrice}
                  onChange={(e) => setForm((p) => ({ ...p, vehiclePrice: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Desconto"
                  value={form.discount}
                  onChange={(e) => setForm((p) => ({ ...p, discount: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Troca / abatimento"
                  value={form.tradeInValue}
                  onChange={(e) => setForm((p) => ({ ...p, tradeInValue: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Taxas / serviços"
                  value={form.fees}
                  onChange={(e) => setForm((p) => ({ ...p, fees: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Entrada"
                  value={form.downPayment}
                  onChange={(e) => setForm((p) => ({ ...p, downPayment: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Parcelas (qtd.)"
                  value={form.installmentsCount}
                  onChange={(e) => setForm((p) => ({ ...p, installmentsCount: toNum(e.target.value) }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Valor da parcela (opcional)"
                  value={form.installmentsValue}
                  onChange={(e) => setForm((p) => ({ ...p, installmentsValue: toNum(e.target.value) }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Observações"
                  value={form.notes || ''}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Resumo
            </Typography>

            {!selectedVehicle ? (
              <Alert severity="info">Selecione um veículo para ver o resumo e exportar a proposta.</Alert>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedVehicle.brand} {selectedVehicle.model} ({selectedVehicle.year})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.25 }}>
                  ID: {selectedVehicle.id} • Placa/Ident.: {(selectedVehicle as any).plate || (selectedVehicle as any).identification || '—'}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Valor do veículo
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrencyBR(computed.vehiclePrice)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Desconto
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    − {formatCurrencyBR(computed.discount)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Troca / abatimento
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    − {formatCurrencyBR(computed.tradeInValue)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Taxas / serviços
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrencyBR(computed.fees)}
                  </Typography>

                  <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {formatCurrencyBR(computed.total)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Entrada
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrencyBR(computed.downPayment)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Saldo a pagar
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrencyBR(computed.remaining)}
                  </Typography>

                  {computed.installmentsCount > 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Parcelas
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {computed.installmentsCount}× {formatCurrencyBR(computed.finalInstallmentValue)}
                      </Typography>
                    </>
                  ) : null}
                </Box>

                <Alert severity={canExportPdf ? 'success' : 'warning'} sx={{ mt: 2 }}>
                  {canExportPdf
                    ? 'Tudo pronto. Clique em “Exportar PDF”.'
                    : 'Para exportar, selecione um veículo e preencha o nome do cliente.'}
                </Alert>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProposalPage;

